import { faCircle, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import {
    faHorizontalRule,
    faSquareDashed,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    BoxProps,
    ButtonProps,
    DividerProps,
    Box as MuiBox,
    Divider as MuiDivider,
    IconButton as MuiIconButton,
    styled,
    useTheme,
} from "@mui/material";
import React, { useContext } from "react";

import { FormContext } from "src/contexts/form";
import { MapContext } from "src/contexts/map";
import AddElement from "src/forms/AddElement";
import { Enum_Action_Type, Enum_Geometry_Types } from "src/types";

const Box = styled(MuiBox)<BoxProps>(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1),
}));

const Divider = styled(MuiDivider)<DividerProps>(({ theme }) => ({
    marginLeft: theme.spacing(2),
}));

const IconButton = styled(MuiIconButton)<ButtonProps>(({ theme }) => ({
    height: theme.spacing(3),
    width: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    "&:hover": {
        backgroundColor: theme.palette.common.white,
        boxShadow: "none",
    },
}));

type Type_Icon_Definition = {
    icon: IconDefinition;
    type: Enum_Geometry_Types;
    sx: {
        [key: string]: string;
    };
};

type Type_Props_Form_Options = {
    addElement: boolean;
    onCloseForm: () => void;
};

const FormOptions: React.FC<Type_Props_Form_Options> = ({
    addElement,
    onCloseForm,
}: Type_Props_Form_Options) => {
    const { updateType, openForm, updateActionType } = useContext(FormContext);
    const { updateNewElement, categories } = useContext(MapContext);
    const theme = useTheme();

    // this detects the types used by categories
    // it helps to display the apropriate drawing options
    const typesDetectedFromCategories = [
        ...new Set(
            categories
                .filter((item) => item.nature !== null)
                .map((item) => item.nature),
        ),
    ];

    const handleIconClick = (value: Enum_Geometry_Types) => {
        updateActionType(Enum_Action_Type.create);
        updateNewElement(null);
        updateType(value);
        if (typesDetectedFromCategories.length > 1) onCloseForm();
    };

    const iconsList: Type_Icon_Definition[] = [
        {
            type: Enum_Geometry_Types.POLYGON,
            icon: faSquareDashed,
            sx: {
                height: theme.spacing(4),
            },
        },
        {
            type: Enum_Geometry_Types.LINESTRING,
            icon: faHorizontalRule,
            sx: {
                height: theme.spacing(4),
            },
        },
        {
            type: Enum_Geometry_Types.POINT,
            icon: faCircle,
            sx: {
                height: theme.spacing(1),
            },
        },
    ];

    const selectedIconsList = iconsList.filter((icon) =>
        typesDetectedFromCategories.includes(icon.type),
    );

    return (
        <>
            {addElement && !openForm && (
                <Box>
                    {selectedIconsList.map((item, index) => (
                        <MuiBox
                            key={index}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <IconButton
                                disableRipple
                                disableFocusRipple
                                onClick={() => handleIconClick(item.type)}
                            >
                                <FontAwesomeIcon
                                    style={item.sx}
                                    icon={item.icon}
                                />
                            </IconButton>
                            {index < selectedIconsList.length - 1 && (
                                <Divider orientation="vertical" flexItem />
                            )}
                        </MuiBox>
                    ))}
                </Box>
            )}

            {openForm && <AddElement />}
        </>
    );
};

export default FormOptions;
